import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";

const fmtN = (v) => Number(v || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 });
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Buscador por CN / nombre / G.H. sobre todo el estudio (genérico + marca)
export function StudyCnSearch({ items, onGoTo }) {
  const [q, setQ] = useState("");
  const all = useMemo(() => [
    ...(items?.generico || []).map((i) => ({ ...i, _kind: "generico" })),
    ...(items?.marca || []).map((i) => ({ ...i, _kind: "marca" })),
  ], [items]);
  const term = q.trim();
  const results = useMemo(() => {
    if (term.length < 3) return [];
    const digits = term.replace(/\D/g, "");
    const isCn = digits.length >= 3 && digits.length === term.replace(/\s/g, "").length;
    const t = norm(term);
    return all.filter((i) => (isCn
      ? String(i.cn || "").startsWith(digits.slice(0, 6))
      : norm(i.name).includes(t) || norm(i.gh).includes(t))).slice(0, 30);
  }, [all, term]);

  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-4" data-testid="study-cn-search">
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en este estudio por CN, nombre o molécula (ej.: 732058)"
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          data-testid="study-cn-search-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="study-cn-search-clear" aria-label="Borrar búsqueda">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {term.length >= 3 && (
        <div className="mt-3" data-testid="study-cn-search-results">
          {results.length === 0 ? (
            <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="study-cn-search-empty">
              «{term}» no aparece en este estudio. Puede que no se vendiera en este periodo: prueba en otro estudio.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-72 overflow-auto">
              {results.map((i) => (
                <li key={`${i._kind}-${i.cn}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm" data-testid={`study-cn-result-${i.cn}`}>
                  <span className="font-mono-num text-slate-500 w-16">{i.cn}</span>
                  <span className="flex-1 min-w-[12rem] text-slate-800">{i.name}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${i._kind === "marca" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {i._kind === "marca" ? "Marca" : "Genérico"}
                  </span>
                  <span className="font-mono-num font-bold w-20 text-right">{fmtN(i.units)} uds</span>
                  <span className="font-mono-num text-slate-600 w-20 text-right">{fmtN(i.euros)} €</span>
                  <button onClick={() => onGoTo(i)} className="text-xs font-semibold text-sky-700 hover:underline" data-testid={`study-cn-goto-${i.cn}`}>
                    Ver en la tabla
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
